import React, { useEffect, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Table,
  Button,
  Space,
  Tag,
  Tooltip,
  Typography,
  Modal,
  Empty,
  Descriptions,
  Divider
} from 'antd';
import {
  PlusOutlined,
  EditOutlined,
  DeleteOutlined,
  InfoCircleOutlined,
  FilterOutlined,
  GlobalOutlined,
} from '@ant-design/icons';
import type { ColumnsType } from 'antd/es/table';
import { useAppDispatch, useAppSelector } from 'store';
import {
  fetchGlobalSegments,
  deleteGlobalSegment,
  selectGlobalSegments,
  selectGlobalSegmentsLoading,
} from 'store/slices/globalSegments.slice';
import { Segment, SegmentCondition } from 'types/push.types';
import { formatters } from 'utils/formatters';
import { notify } from 'utils/helpers';
import { ROUTES } from 'config/routes';

const { confirm } = Modal;
const { Text, Title } = Typography;

const fieldLabels: Record<string, string> = {
  has_registration: 'Регистрация',
  has_deposit: 'Депозит',
  locale: 'Локаль',
  country: 'Страна',
  os: 'ОС',
  offer: 'Оффер',
  buyer_id: 'Байер',
  install_date: 'Дата установки',
};

const operatorLabels: Record<string, string> = {
  equals: '=',
  greater_than: '>',
  less_than: '<',
  contains: 'содержит',
  not_contains: 'не содержит',
};

const getConditionDisplay = (condition: SegmentCondition): string => {
  const field = fieldLabels[condition.field] || condition.field;
  const operator = operatorLabels[condition.operator] || condition.operator;
  let value = condition.value;
  if (typeof value === 'boolean') value = value ? 'Да' : 'Нет';
  return `${field} ${operator} ${value}`;
};

const ExpandedRow: React.FC<{ record: Segment }> = ({ record }) => {
  const conditions = record.conditions || [];

  return (
    <div
      style={{
        padding: '8px 48px 16px',
        background: '#fafafa',
        borderRadius: 6,
      }}
    >
      <Space size={32} align="start" wrap>
        <div>
          <Space style={{ marginBottom: 8 }}>
            <FilterOutlined style={{ color: '#1890ff' }} />
            <Text strong>Условия сегмента</Text>
            <Text type="secondary">({conditions.length})</Text>
          </Space>
          {conditions.length === 0 ? (
            <div>
              <Tag>Без условий — все пользователи</Tag>
            </div>
          ) : (
            <Space size={6} wrap>
              {conditions.map((cond, idx) => (
                <Tag key={idx} color="blue" style={{ marginBottom: 4 }}>
                  {getConditionDisplay(cond)}
                </Tag>
              ))}
            </Space>
          )}
        </div>

        <Divider
          type="vertical"
          style={{ height: 'auto', alignSelf: 'stretch' }}
        />

        <div>
          <Descriptions column={1} size="small" style={{ minWidth: 220 }}>
            <Descriptions.Item label="Создан">
              {formatters.date(record.created_at)}
            </Descriptions.Item>
            <Descriptions.Item label="Обновлён">
              {formatters.timeAgo(record.updated_at)}
            </Descriptions.Item>
          </Descriptions>
        </div>

        {record.description && (
          <>
            <Divider
              type="vertical"
              style={{ height: 'auto', alignSelf: 'stretch' }}
            />
            <div style={{ maxWidth: 320 }}>
              <Text strong style={{ display: 'block', marginBottom: 6 }}>
                Описание
              </Text>
              <Text type="secondary">{record.description}</Text>
            </div>
          </>
        )}
      </Space>
    </div>
  );
};

const GlobalSegmentsListPage: React.FC = () => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();

  const segments = useAppSelector(selectGlobalSegments);
  const loading = useAppSelector(selectGlobalSegmentsLoading);

  const [expandedRowKeys, setExpandedRowKeys] = useState<React.Key[]>([]);

  useEffect(() => {
    dispatch(fetchGlobalSegments());
  }, [dispatch]);

  const handleDelete = (segment: Segment) => {
    confirm({
      title: 'Удалить глобальный сегмент?',
      content: `Вы уверены, что хотите удалить сегмент "${segment.name}"? Это действие нельзя отменить.`,
      okText: 'Удалить',
      okType: 'danger',
      cancelText: 'Отмена',
      onOk: async () => {
        try {
          await dispatch(deleteGlobalSegment(segment.id)).unwrap();
          notify.success('Глобальный сегмент удалён');
        } catch (error: any) {
          notify.error(error.message || error || 'Ошибка при удалении');
        }
      },
    });
  };

  const columns: ColumnsType<Segment> = [
    {
      title: 'Название',
      dataIndex: 'name',
      key: 'name',
      render: (name: string) => (
        <Space>
          <GlobalOutlined style={{ color: '#722ed1' }} />
          <Text strong style={{ fontSize: 14 }}>
            {name}
          </Text>
        </Space>
      ),
    },
    {
      title: 'Условий',
      key: 'conditions_count',
      width: 110,
      align: 'center',
      render: (_, record) => {
        const len = record.conditions?.length ?? 0;
        return (
          <Tag color={len === 0 ? 'default' : 'blue'}>
            {len === 0 ? 'Без условий' : `${len} усл.`}
          </Tag>
        );
      },
    },
    {
      title: 'Обновлён',
      dataIndex: 'updated_at',
      key: 'updated_at',
      width: 160,
      render: (date: string) => (
        <Text type="secondary">{formatters.timeAgo(date)}</Text>
      ),
    },
    {
      title: 'Действия',
      key: 'actions',
      width: 100,
      fixed: 'right',
      render: (_, record) => (
        <Space size="small">
          <Tooltip title="Редактировать">
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={e => {
                e.stopPropagation();
                navigate(
                  ROUTES.GLOBAL_SEGMENT_EDIT.replace(':id', String(record.id)),
                );
              }}
            />
          </Tooltip>
          <Tooltip title="Удалить">
            <Button
              type="text"
              danger
              icon={<DeleteOutlined />}
              onClick={e => {
                e.stopPropagation();
                handleDelete(record);
              }}
            />
          </Tooltip>
        </Space>
      ),
    },
  ];

  return (
    <div style={{ padding: 24 }}>
      <div
        style={{
          marginBottom: 16,
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
        }}
      >
        <div>
          <Space>
            <GlobalOutlined style={{ color: '#722ed1', fontSize: 20 }} />
            <Title level={3} style={{ margin: 0 }}>
              Глобальные сегменты
            </Title>
          </Space>
          <div>
            <Text type="secondary">
              Сегменты, которые работают со всеми приложениями. Используются в
              глобальных кампаниях.
            </Text>
          </div>
        </div>
        <Button
          type="primary"
          icon={<PlusOutlined />}
          onClick={() => navigate(ROUTES.GLOBAL_SEGMENT_CREATE)}
          size="large"
        >
          Создать глобальный сегмент
        </Button>
      </div>

      <Table
        columns={columns}
        dataSource={segments}
        loading={loading}
        rowKey="id"
        onRow={record => ({
          onClick: () => {
            setExpandedRowKeys(prev =>
              prev.includes(record.id)
                ? prev.filter(k => k !== record.id)
                : [...prev, record.id],
            );
          },
          style: { cursor: 'pointer' },
        })}
        expandable={{
          expandedRowKeys,
          expandedRowRender: record => <ExpandedRow record={record} />,
          onExpand: (expanded, record) => {
            setExpandedRowKeys(prev =>
              expanded
                ? [...prev, record.id]
                : prev.filter(k => k !== record.id),
            );
          },
          rowExpandable: () => true,
        }}
        pagination={{
          pageSize: 20,
          showSizeChanger: true,
          showTotal: total => `Всего сегментов: ${total}`,
        }}
        locale={{
          emptyText: (
            <Empty
              image={Empty.PRESENTED_IMAGE_SIMPLE}
              description={
                <Space direction="vertical">
                  <Text>Глобальные сегменты не созданы</Text>
                  <Text type="secondary">
                    Создайте первый глобальный сегмент для использования в
                    кросс-app кампаниях
                  </Text>
                </Space>
              }
            />
          ),
        }}
        scroll={{ x: 900 }}
      />

      {segments.length > 0 && (
        <div style={{ marginTop: 16 }}>
          <Space>
            <InfoCircleOutlined style={{ color: '#1890ff' }} />
            <Text type="secondary" style={{ fontSize: 12 }}>
              Нажмите на строку, чтобы развернуть детали сегмента.
            </Text>
          </Space>
        </div>
      )}
    </div>
  );
};

export default GlobalSegmentsListPage;